/**
 * Shared open/closed state for the AI Coach right rail.
 * Persisted in localStorage so the user's choice survives reloads, and exposed
 * via useSyncExternalStore so the drawer and the OKR detail page (which has to
 * reserve right-padding when the rail is open) stay in sync.
 *
 * Default: closed — the rail must not cover page content on first visit.
 */

import { useCallback, useSyncExternalStore } from 'react';

const STORAGE_KEY = 'okrhub.aiCoachDrawer.open';

const listeners = new Set<() => void>();

const read = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const subscribe = (cb: () => void) => {
  listeners.add(cb);
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) cb();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(cb);
    window.removeEventListener('storage', onStorage);
  };
};

const getServerSnapshot = () => false;

type Setter = boolean | ((prev: boolean) => boolean);

export const useAICoachDrawerOpen = (): [boolean, (next: Setter) => void] => {
  const open = useSyncExternalStore(subscribe, read, getServerSnapshot);
  const setOpen = useCallback((next: Setter) => {
    const value = typeof next === 'function' ? (next as (p: boolean) => boolean)(read()) : next;
    try {
      localStorage.setItem(STORAGE_KEY, String(value));
    } catch {
      /* ignore */
    }
    listeners.forEach((l) => l());
  }, []);
  return [open, setOpen];
};
